import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  createFilterInteractionDataLayer,
  createSearchResultsDataLayer,
  createSearchDataLayer
} from "@/factories/analytics/data-layer-factory";
import { TourSearchResult, SearchSettings } from "@/types/search/tour-search-results";



interface UseSearchDataLayerProps {
  searchResults: TourSearchResult[];
  searchSettings: SearchSettings | undefined;
  initialSearchParams?: { [key: string]: string | string[] | undefined };
  searchSource: string;
  isLoading: boolean;
  isError: boolean;
  userGroups: string[];
}

interface FilterInteraction {
  filterType: string;
  filterValue: string | string[];
  action: "add" | "remove" | "clear" | "sort" | "page";
}

const useSearchDataLayer = ({
  searchResults,
  searchSettings,
  initialSearchParams,
  searchSource,
  isLoading,
  isError,
  userGroups
}: UseSearchDataLayerProps) => {
  const [filterDataLayerData, setFilterDataLayerData] = useState<any>(null);
  const [searchDataLayerData, setSearchDataLayerData] = useState<any>(null);
  const lastFilterInteraction = useRef<FilterInteraction | null>(null);

  // Helper function for checking roles
  const hasRole = useCallback((role: string) => userGroups.includes(role), [userGroups]);
  const isAgentOrProfessional = useMemo(() =>
    hasRole("Agent") || hasRole("TravelProfessional"),
    [hasRole]
  );

  // Generate search results dataLayer data
  const searchResultsDataLayer = useMemo(() => {
    if (typeof window !== "undefined") {
      const fullUrl = window.location.href;
      const noFragmentQuery = !fullUrl.includes("#q/");
      const dataReady = !isLoading && !isError && searchSettings && searchResults !== undefined;

      if (noFragmentQuery && dataReady) {
        return createSearchResultsDataLayer(
          initialSearchParams,
          searchSettings.totalCount,
          fullUrl,
          searchSource,
          searchResults
        );
      }
    }
    return null;
  }, [searchResults, searchSettings, initialSearchParams, searchSource, isLoading, isError]);

  // Handle filter interactions (store for later processing)
  const handleFilterInteraction = useCallback((
    filterType: string,
    filterValue: string | string[],
    action: "add" | "remove" | "clear" | "sort" | "page"
  ) => {
    // Store the interaction but don't send to dataLayer yet
    lastFilterInteraction.current = { filterType, filterValue, action };
  }, []);

  // Process filter interactions and send to dataLayer when search results are updated
  useEffect(() => {
    if (lastFilterInteraction.current && !isLoading && searchSettings && searchResults) {
      const interaction = lastFilterInteraction.current;

      // Debug: Filter interaction being sent to dataLayer
      // console.log("Sending filter interaction to dataLayer:", {
      //   ...interaction,
      //   resultsCount: searchSettings.totalCount
      // });

      // Create filter interaction data layer
      const filterDataLayerEvent = createFilterInteractionDataLayer(
        searchSettings.totalCount,
        interaction.filterType,
        interaction.filterValue,
        isAgentOrProfessional,
        interaction.action
      );

      // Create search data layer with the filter value as search term
      const searchDataLayerEvent = createSearchDataLayer(interaction.filterValue);

      // Send both events
      setFilterDataLayerData(filterDataLayerEvent);
      setSearchDataLayerData(searchDataLayerEvent);

      // Clear data after a short delay to prevent duplicate sends
      const timeoutId = setTimeout(() => {
        setFilterDataLayerData(null);
        setSearchDataLayerData(null);
      }, 100);

      // Clear the stored interaction
      lastFilterInteraction.current = null;

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isLoading, searchSettings, searchResults, isAgentOrProfessional]);

  // Clear dataLayer data manually (useful for cleanup)
  const clearDataLayerData = useCallback(() => {
    setFilterDataLayerData(null);
    setSearchDataLayerData(null);
    lastFilterInteraction.current = null;
  }, []);

  return {
    searchResultsDataLayer,
    filterDataLayerData,
    searchDataLayerData,
    handleFilterInteraction,
    clearDataLayerData
  };
};

export default useSearchDataLayer;